import { AlertCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth.context";
import { FORGOT_PASSWORD_ROUTE } from "@/lib/auth-routes";

export interface DuplicateDocumentInfo {
  documentType: string;
  message: string;
  suggestion: string;
}

interface DuplicateDocumentDialogProps {
  open: boolean;
  info: DuplicateDocumentInfo | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Modal do checkout para CPF/CNPJ que já pertence a outra conta.
 *
 * "Fazer Login" e "Recuperar Senha" encerram a sessão da conta recém-cadastrada
 * antes de seguir: com ela viva, o guard de `/login` (GuestRoute) devolveria o
 * usuário ao app — direto ou pelo "Voltar ao login" da recuperação — em vez de
 * deixá-lo entrar na conta existente.
 */
export const DuplicateDocumentDialog = ({ open, info, onOpenChange }: DuplicateDocumentDialogProps) => {
  const { logout } = useAuth();

  const fechar = () => onOpenChange(false);

  const irParaLogin = () => {
    fechar();
    void logout();
  };

  const irParaRecuperacaoDeSenha = () => {
    fechar();
    void logout({ redirectTo: FORGOT_PASSWORD_ROUTE });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            {info?.documentType} já cadastrado
          </AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground">
            {info?.message}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4">
          <p className="text-sm text-muted-foreground mb-4">{info?.suggestion}</p>

          <div className="flex flex-col gap-3">
            <Button onClick={irParaLogin} className="w-full">
              Fazer Login
            </Button>

            <Button variant="outline" onClick={irParaRecuperacaoDeSenha} className="w-full">
              Recuperar Senha
            </Button>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogAction
            onClick={fechar}
            className="bg-muted text-muted-foreground hover:bg-muted/80"
          >
            Fechar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
